/**
 * 이상한 방 - HUD (순수 DOM)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.3절(거부는 UI에서 처리) · 컨셉시트 4.2절(동사 즉시 실행 UI)
 *           컨셉시트 4.3절(시작 탄환 6발 / 오답 1발 소모 / 사격 불가 → 관전)
 *
 * ## 프레임워크를 쓰지 않는 이유
 *
 * HUD가 다루는 상태는 열 개 남짓이고 전부 `render`/`renderGame` 한 번으로 반영된다.
 * 여기에 런타임을 얹으면 번들이 커지는데, 7.0절이 웹 스택을 고른 근거가
 * "링크 하나로 6명이 즉시 접속"이다. 그 근거를 스스로 갉아먹을 이유가 없다.
 *
 * ## 두 개의 HUD가 한 파일에 있는 이유
 *
 * 앱에는 모드가 둘이다 - **관찰(찾을 때)**과 **배치(만들 때)**. 둘의 HUD는 겹치지 않는다:
 * 배치는 카탈로그·팔레트·셀 좌표가 필요하고, 관찰은 탄약·모드·시드만 필요하다.
 * 그래서 `render(view)`(배치)와 `renderGame(view)`(공통·관찰)를 나누되,
 * **토스트·마운트·수명은 하나**로 둔다. HUD 루트가 둘이면 z-index와 dispose 순서를 매번 맞춰야 하고
 * 그 실수는 "패널이 두 겹으로 쌓여 클릭이 안 먹는다"로 나타난다.
 *
 * 모드가 시점(1인칭↔에디터)이 아니라 **탭의 의미**로 재정의된 것이 아이소메트릭 전환의 결과다.
 * 카메라는 하나뿐이고 양쪽 모드에서 똑같이 돌고 줌한다. 달라지는 것은 "탭하면 지목인가 배치인가"뿐이다.
 *
 * ## 게임 UI로 보이게 하는 것은 무엇인가
 *
 * 개발자 도구처럼 보이는 원인은 색이 아니라 **정보 배치**다. 라벨:값 표를 화면 구석에
 * 늘어놓으면 그건 디버그 패널이고, 값 하나를 크게 놓고 나머지를 지우면 게임 UI다.
 * 그래서 관찰 모드에서는 탄약만 화면 하단에 크게 두고(숫자 + 탄피 표식 이중 표기),
 * 시드·소품 수는 흐린 칩 하나로 접었다. 진단 정보는 남기되 **시선의 우선순위에서 내린다.**
 *
 * ## 모바일이 기준선이다
 *
 * 이 HUD의 레이아웃 기준은 데스크톱이 아니라 **폰**이다(`hud.css`의 반응형 분기).
 * 좌측 세로 목록은 폰에서 화면의 절반을 먹으므로 좁은 화면에서는 하단 가로 스크롤 시트가 되고,
 * 모든 누르는 것은 44×44px 이상을 지킨다. 그 크기는 미관이 아니라 **입력 성공률**이며,
 * 이 프로젝트의 진짜 병목이 "6명 모으기"라는 판단(9.4절)에서 곧바로 따라 나온다.
 *
 * ## 이 파일의 책임 경계
 *
 * HUD는 **표시와 클릭 전달만** 한다. 게임 상태도, 검증도, 키 처리도 갖지 않는다.
 * 상태를 조금이라도 들고 있으면 "HUD에는 8개인데 실제로는 9개"가 발생하고,
 * 이 게임에서 그 불일치는 곧 판정 불신이 된다.
 *
 * DOM 노드는 만들 때 한 번만 조립하고 이후에는 텍스트·클래스만 갈아 끼운다.
 * 포인터가 움직일 때마다 `render`가 도는데 매번 재생성하면 그 자체가 프레임 예산을 먹는다.
 *
 * ## 문구는 이 파일이 정하지 않는다 (2026-09-01 변경)
 *
 * 화면에 나가는 말은 전부 `i18n`의 `t()`를 거친다. 여기 한국어를 박아 두면 영어 사전과
 * 갈라지고, 갈라진 문구는 아무도 눈치채지 못한 채 배포까지 간다(`i18n/GLOSSARY.md`).
 * 용어도 그 문서가 정한다 - '시드'가 아니라 **방 번호**, '오브젝트'가 아니라 **소품**이다.
 *
 * 그 정리에서 **셀 좌표 표시가 사라졌다.** `셀 14, 0, 17`은 플레이어가 할 수 있는 일이 없는
 * 개발자 라벨이고, 용어집 2절이 이름을 붙여 금지한 항목이다. 그리드 감각이 필요하면
 * 화면의 격자 오버레이(`render/gridHelper.ts`)가 이미 그 일을 한다.
 */

import type { CatalogEntry, CatalogId, Cell3, PaletteIndex } from '../core/types'
import type { PaletteColor } from '../catalog/palette'
import type { VerbRejection } from '../core/verbs'
import { hasKey, onLocaleChange, t, type MessageKey } from '../i18n'
import { createElement, createRepaintRegistry, setText, toCssColor, type Repaint } from './dom'
import type { ShotFeedback } from './mobileControls'

import './hud.css'

/** 토스트 표시 시간(ms). 1분짜리 페이즈라 오래 남으면 그 자체가 시야 방해다. */
const TOAST_DURATION_MS = 1800

/** 회전 동사가 잠기는 대칭 차수(4.2절: 4 = 완전 대칭 → 회전 전면 거부). */
const FULLY_SYMMETRIC = 4

/**
 * 거부 사유의 문구 키.
 *
 * 사전 키가 사유 id를 그대로 물고 있어서(`editor.reject.cell-occupied`) 분기 없이 조립된다.
 * `i18n/GLOSSARY.md` 4.2절이 그러라고 이름을 맞춰 둔 자리다 - 여기서 `switch`를 새로 쓰면
 * 사유가 하나 늘 때마다 사전과 코드 두 곳을 고쳐야 하고, 한쪽만 고친 날이 반드시 온다.
 *
 * 문구가 짧은 것은 스타일이 아니다. 체인저는 1분에 10개를 처리해야 하고(4.3절) 술래도
 * 꾸미기 1분 안에 배치를 끝내야 한다. 읽는 데 2초 걸리는 문구는 알려 주지 않는 것과 같다.
 */
function rejectionKey(reason: VerbRejection): string {
  return `editor.reject.${reason}`
}

/**
 * 조작 안내(데스크톱 전용 - 폰에서는 화면을 먹기만 하므로 접는다).
 * 키와 설명을 한 곳에 모아 두어 `editor.ts`·`pointerInput.ts`의 키 처리와 문구가 갈라지지 않게 한다.
 *
 * 왼쪽 칸에는 두 종류가 섞인다. `Tab`·`Q / E`처럼 **자판에 새겨진 글자**는 번역 대상이 아니므로
 * 그대로 쓰고, `탭`·`드래그`처럼 손동작을 부르는 말만 사전에서 가져온다.
 * 둘을 가르는 것은 `hasKey`다 - 사전 키에는 점이 들어가므로 자판 글자와 섞일 일이 없다.
 */
const KEY_GUIDE: readonly Readonly<{ key: string; desc: MessageKey }>[] = [
  { key: 'Tab', desc: 'controls.mode' },
  { key: 'controls.key.tap', desc: 'controls.tap' },
  { key: 'controls.key.tapAgain', desc: 'controls.confirm' },
  { key: 'controls.key.drag', desc: 'controls.drag' },
  { key: 'controls.key.wheel', desc: 'controls.zoom' },
  { key: 'Q / E', desc: 'controls.turnView' },
  { key: 'R', desc: 'controls.rotateProp' },
  { key: 'C', desc: 'controls.nextColor' },
  { key: 'X / Delete', desc: 'controls.removeProp' },
  { key: 'Shift / Alt', desc: 'controls.freeHeight' },
  { key: '↑ / ↓', desc: 'controls.heightAdjust' },
  { key: '1 ~ 0', desc: 'controls.quickPick' },
  { key: 'Esc', desc: 'controls.deselect' },
]

/** 자판 글자면 그대로, 사전에 있는 말이면 지금 언어로. 위 표의 왼쪽 칸을 그리는 규칙이다. */
function keyLabel(key: string): string {
  return hasKey(key) ? t(key) : key
}

/* --- 관찰(수색) HUD ------------------------------------------------------------- */

/**
 * 앱의 두 모드. **시점이 아니라 탭의 의미**를 가른다 -
 * 카메라는 아이소메트릭 하나뿐이고 양쪽에서 똑같이 회전·줌한다.
 */
export type HudMode = 'observe' | 'place'

/**
 * 잔탄 경고 문턱. 컨셉시트 4.3의 시작 탄환이 6발이므로 2발이면 **3분의 1이 남은 시점**이다.
 * 여기서부터 색과 맥박을 바꿔 "이제 함부로 못 쏜다"를 몸으로 알게 한다 -
 * 숫자만 보고 판단하라고 하면 화면 중앙을 보던 눈이 매번 아래로 내려와야 한다.
 */
const LOW_AMMO_THRESHOLD = 2

/** 탄피 표식의 최대 개수. 이보다 많으면 한눈에 세어지지 않아 숫자만 남긴다. */
const MAX_AMMO_PIPS = 12

/**
 * 사격 결과 문구의 키. 6.1절의 두 히트 경로를 **다른 말**로 가른다
 * (무엇을 맞혔는지 알아야 다음 추론이 선다). 사유 id가 곧 키의 가운데 토막이다.
 */
function shotKey(feedback: ShotFeedback): string {
  return `shot.${feedback}.label`
}

/**
 * 모드 배지 문구. 라벨이 아니라 **지금 탭이 무엇을 하는지**를 말한다.
 * `탭 = 지목` 같은 등호 라벨을 쓰지 않는 이유는 용어집 2절에 있다 - 라벨과 값을 등호로 이으면
 * 개발자 로그처럼 읽힌다. 그래서 힌트도 문장이다.
 */
const MODE_MESSAGE: Readonly<Record<HudMode, { label: MessageKey; hint: MessageKey }>> = {
  observe: { label: 'editor.modeLook', hint: 'editor.tapShoots' },
  place: { label: 'editor.modePlace', hint: 'editor.tapPlaces' },
}

/** 관찰·공통 HUD가 그리는 데 필요한 전부. */
export type GameHudView = Readonly<{
  mode: HudMode
  /** 남은 탄환. 4.3절: 시작 6발, 오답 1발 소모, 0이면 사격 불가(관전). */
  ammo: number
  ammoMax: number
  /** 방 생성 시드. 같은 시드면 같은 방이므로 버그 재현의 유일한 열쇠다. */
  seed: string
  objectCount: number
  /** 마지막 지목 결과. 아직 쏘지 않았으면 `null`. */
  lastShot: ShotFeedback | null
}>

/** HUD가 바깥으로 내보내는 유일한 출력. 전부 "사용자가 무엇을 눌렀다"뿐이다. */
export type HudHandlers = Readonly<{
  onPickCatalog: (id: CatalogId) => void
  onPickSelectTool: () => void
  onPickColor: (id: PaletteIndex) => void
}>

/** HUD가 그리는 데 필요한 전부. 여기에 없는 값은 HUD가 알 필요도 없는 값이다. */
export type HudView = Readonly<{
  /** 'place' = 카탈로그 소품을 놓는 중 / 'select' = 이미 놓인 물건을 다루는 중. */
  toolKind: 'place' | 'select'
  activeCatalogId: CatalogId | null
  /** 배치 모드에서는 "새로 놓을 색", 선택 모드에서는 "선택한 물건의 색". */
  activeColorId: PaletteIndex
  selectedName: string | null
  /** 선택한 물건이 `rotSymmetry: 4`인가(회전 동사 잠금 → 4.2절). */
  isRotateLocked: boolean
  placedCount: number
  placementLimit: number
  isFreeHeight: boolean
  /** 자유 높이 모드에서 잠긴 그리드 층. 바닥에 붙은 상태면 `null`. */
  freeHeightLevel: number | null
  /**
   * 지금 포인터가 가리키는 셀.
   *
   * **화면에 그리지 않는다.** `셀 14, 0, 17`은 플레이어가 이 숫자로 할 수 있는 일이 없는
   * 개발자 라벨이고, 용어집 2절이 이름을 붙여 금지한 항목이다(그래서 사전에 키도 없다).
   * 필드가 남아 있는 것은 `editor.ts`의 `buildHudView`가 아직 이 값을 넘기기 때문이며,
   * 그쪽을 손대는 사람이 이 줄과 그 줄을 같이 지우면 된다.
   */
  pointerCell: Cell3 | null
}>

export type HudHandle = Readonly<{
  /** 에디터 패널 갱신. 관찰 모드에서는 패널 자체가 숨겨지지만 호출해도 무해하다. */
  render: (view: HudView) => void
  /**
   * 이 HUD 레이어를 통째로 켜고 끈다. **게임 모드 ↔ 배치 모드 전환의 스위치다.**
   *
   * 게임 모드에는 자기 HUD가 따로 있고(`ui/gameHud.ts` - 페이즈 시계·발견 카운터·대형 탄약·
   * 정답 방 버튼), 그쪽이 탄약을 이미 크게 그린다. 두 레이어를 같이 띄우면 화면에 탄약 게이지가
   * 둘이 되고, 한쪽이 라운드 상태를 모른 채 남아 있으면 **어느 숫자가 진짜인지 알 수 없다** -
   * 이 게임에서 그 불일치는 곧 판정 불신이다.
   *
   * `renderGame`을 호출하지 않는 것만으로는 부족하다. 마지막 값이 화면에 그대로 얼어붙기 때문이다.
   */
  setVisible: (visible: boolean) => void
  /** 모드 배지·탄약·시드 등 관찰·공통 표시 갱신. 패널 표시 여부도 `view.mode`가 결정한다. */
  renderGame: (view: GameHudView) => void
  /** 동사 검증이 거부한 이유를 알린다. 문구 결정은 HUD의 몫이다(표시 계층). */
  showRejection: (reason: VerbRejection) => void
  showNotice: (text: string) => void
  dispose: () => void
}>

export type HudOptions = Readonly<{
  mount: HTMLElement
  catalog: readonly CatalogEntry[]
  palette: readonly PaletteColor[]
  handlers: HudHandlers
}>

/* --- DOM 조립 헬퍼 -------------------------------------------------------------- */

function setActive(element: HTMLElement, isActive: boolean): void {
  element.classList.toggle('is-active', isActive)
}

/**
 * 소품이 차지하는 칸 수.
 *
 * `2×1×1` 같은 축별 표기를 버렸다. 그건 풋프린트라는 내부 용어를 화면에 그대로 옮긴 것이고
 * (용어집 2절), 놓으려는 사람이 알고 싶은 것은 축이 아니라 **자리를 얼마나 먹는가**다.
 */
function footprintCells(footprint: Cell3): number {
  return footprint.x * footprint.y * footprint.z
}

/* --- 패널 조립 ------------------------------------------------------------------ */

/** 라벨 + 값 한 줄. 값 노드를 돌려주어 호출부가 갱신 대상만 들고 있게 한다. */
function createStatusRow(parent: HTMLElement, label: MessageKey, repaint: Repaint): HTMLElement {
  const row = createElement('div', 'hud-status__row')
  const labelNode = createElement('span', 'hud-status__label')
  repaint(() => setText(labelNode, t(label)))
  row.append(labelNode)

  const value = createElement('span', 'hud-status__value')
  row.append(value)
  parent.append(row)

  return value
}

/** 패널 제목(노란 자막 태그). 제목도 문구라 언어가 바뀌면 같이 바뀐다. */
function createPanelTitle(key: MessageKey, repaint: Repaint): HTMLElement {
  const title = createElement('h2', 'hud-panel__title')
  repaint(() => setText(title, t(key)))
  return title
}

function createCatalogPanel(
  catalog: readonly CatalogEntry[],
  handlers: HudHandlers,
  repaint: Repaint,
): { panel: HTMLElement; selectButton: HTMLButtonElement; itemButtons: ReadonlyMap<CatalogId, HTMLButtonElement> } {
  const panel = createElement('section', 'hud-panel hud-catalog')
  panel.append(createPanelTitle('editor.catalogTitle', repaint))

  // 배치 도구와 선택 도구를 같은 목록에 두는 이유: 둘은 배타적인 모드이고,
  // 모드가 바뀌면 좌클릭의 의미가 통째로 달라진다. 한 목록에 있어야 그 배타성이 읽힌다.
  const selectButton = createElement('button', 'hud-item') as HTMLButtonElement
  selectButton.type = 'button'
  const selectName = createElement('span', 'hud-item__name')
  repaint(() => setText(selectName, t('editor.selectTool')))
  // 'Esc'는 자판에 새겨진 글자라 번역하지 않는다(위 KEY_GUIDE와 같은 규칙).
  selectButton.append(selectName, createElement('span', 'hud-item__meta', 'Esc'))
  selectButton.addEventListener('click', () => handlers.onPickSelectTool())
  panel.append(selectButton)

  const list = createElement('div', 'hud-catalog__list')
  const itemButtons = new Map<CatalogId, HTMLButtonElement>()

  catalog.forEach((entry, index) => {
    const button = createElement('button', 'hud-item') as HTMLButtonElement
    button.type = 'button'
    // 이름은 카탈로그가 가진 값이라 그대로 쓰고, 칸 수를 붙이는 문장만 사전이 정한다.
    repaint(() => {
      button.title = t('editor.footprint', {
        name: entry.name,
        cells: footprintCells(entry.footprint),
      })
    })

    button.append(createElement('span', 'hud-item__name', entry.name))
    if (entry.rotSymmetry === FULLY_SYMMETRIC) {
      const lock = createElement('span', 'hud-item__lock')
      repaint(() => setText(lock, t('editor.rotateLockedChip')))
      button.append(lock)
    }

    // 숫자 키 안내와 같은 순서다(editor.ts). 10번째는 0으로 접힌다.
    const shortcut = (index + 1) % 10
    button.dataset['shortcut'] = String(shortcut)
    button.addEventListener('click', () => handlers.onPickCatalog(entry.id))

    list.append(button)
    itemButtons.set(entry.id, button)
  })

  panel.append(list)

  return { panel, selectButton, itemButtons }
}

function createPalettePanel(
  palette: readonly PaletteColor[],
  handlers: HudHandlers,
  repaint: Repaint,
): { panel: HTMLElement; swatches: readonly HTMLButtonElement[] } {
  const panel = createElement('section', 'hud-panel hud-palette')
  panel.append(createPanelTitle('editor.colorTitle', repaint))

  const grid = createElement('div', 'hud-palette__grid')
  const swatches: HTMLButtonElement[] = []

  for (const color of palette) {
    const swatch = createElement('button', 'hud-swatch') as HTMLButtonElement
    swatch.type = 'button'
    swatch.style.background = toCssColor(color.hex)
    swatch.title = color.name
    // 색만으로 구별되는 컨트롤이라 스크린리더용 이름을 따로 준다.
    repaint(() => {
      swatch.setAttribute('aria-label', t('editor.colorAria', { name: color.name }))
    })
    swatch.addEventListener('click', () => handlers.onPickColor(color.id))

    grid.append(swatch)
    swatches.push(swatch)
  }

  panel.append(grid)

  return { panel, swatches }
}

function createKeysPanel(repaint: Repaint): HTMLElement {
  const panel = createElement('section', 'hud-panel hud-keys')
  panel.append(createPanelTitle('editor.controlsTitle', repaint))

  for (const guide of KEY_GUIDE) {
    const row = createElement('div', 'hud-keys__row')
    const key = createElement('span', 'hud-keys__key')
    const desc = createElement('span', 'hud-keys__desc')
    repaint(() => {
      setText(key, keyLabel(guide.key))
      setText(desc, t(guide.desc))
    })
    row.append(key, desc)
    panel.append(row)
  }

  return panel
}

/* --- 관찰·공통 패널 조립 ---------------------------------------------------------- */

/** 모드 배지 + 힌트 한 줄. 두 노드를 돌려주어 갱신 대상만 들고 있게 한다. */
type ModeBadge = Readonly<{ root: HTMLElement; label: HTMLElement; hint: HTMLElement }>

function createModeBadge(): ModeBadge {
  const root = createElement('div', 'hud-mode')
  // 두 노드의 글자는 모드에 달렸으므로 여기서 채우지 않는다. `renderGame`이 채운다.
  const label = createElement('span', 'hud-mode__label')
  const hint = createElement('span', 'hud-mode__hint')

  root.append(label, hint)

  return { root, label, hint }
}

/**
 * 방 번호·소품 수 칩. 진단 정보이지만 방 번호는 버그 재현의 유일한 열쇠라 화면에서 지울 수 없다.
 * '시드'라 부르지 않는 이유는 용어집 1.3절에 있다 - 화면에 나오는 이 값의 쓸모는 재현이 아니라
 * **공유**이고, 실제로 하는 말은 "이 방 해봐"다.
 */
type SessionChip = Readonly<{ root: HTMLElement; seed: HTMLElement; count: HTMLElement }>

function createSessionChip(repaint: Repaint): SessionChip {
  const root = createElement('div', 'hud-session')

  const seedBox = createElement('div', 'hud-session__item')
  const seedKey = createElement('span', 'hud-session__key')
  const seed = createElement('span', 'hud-session__value')
  repaint(() => setText(seedKey, t('editor.roomCode')))
  seedBox.append(seedKey, seed)

  const countBox = createElement('div', 'hud-session__item')
  const countKey = createElement('span', 'hud-session__key')
  const count = createElement('span', 'hud-session__value')
  repaint(() => setText(countKey, t('editor.propCount')))
  countBox.append(countKey, count)

  root.append(seedBox, countBox)

  return { root, seed, count }
}

/**
 * 탄약 표시.
 *
 * 숫자와 탄피 표식을 **둘 다** 둔다. 숫자는 정확하지만 읽는 데 시선 고정이 필요하고,
 * 표식은 개수를 세지 않아도 "줄어들었다"가 주변시로 잡힌다.
 * 4.3절이 탄환을 게임의 압박 장치로 삼았으므로, 잔탄은 화면을 보지 않고도 느껴져야 한다.
 */
type AmmoGauge = Readonly<{
  root: HTMLElement
  value: HTMLElement
  max: HTMLElement
  pips: HTMLElement
  status: HTMLElement
}>

function createAmmoGauge(): AmmoGauge {
  const root = createElement('div', 'hud-ammo')

  const pips = createElement('div', 'hud-ammo__pips')

  const readout = createElement('div', 'hud-ammo__readout')
  const value = createElement('span', 'hud-ammo__value', '')
  const max = createElement('span', 'hud-ammo__max', '')
  readout.append(value, max)

  // 사격 결과는 조작 직후에 뜬다. 스크린리더가 흐름을 끊지 않고 읽도록 polite로 둔다
  // (크로스헤어 쪽 연출은 aria-hidden이므로 접근 가능한 통지는 여기 하나뿐이다).
  const status = createElement('div', 'hud-ammo__status', '')
  status.setAttribute('role', 'status')
  status.setAttribute('aria-live', 'polite')

  root.append(pips, readout, status)

  return { root, value, max, pips, status }
}

/**
 * 탄피 표식 개수를 맞춘다. 최대 탄약은 라운드 설정이라 바뀔 수 있으므로(5.3절과 같은 성격)
 * 고정 개수로 굽지 않고 필요할 때만 늘리고 줄인다.
 */
function syncAmmoPips(pips: HTMLElement, max: number): void {
  const target = Math.max(0, Math.min(max, MAX_AMMO_PIPS))

  while (pips.childElementCount > target) pips.lastElementChild?.remove()
  while (pips.childElementCount < target) pips.append(createElement('span', 'hud-ammo__pip'))
}

/** 남은 탄약만큼만 표식을 켠다. 쓴 탄피는 지우지 않고 비운다 - 총 몇 발짜리였는지가 같이 보여야 한다. */
function paintAmmoPips(pips: HTMLElement, ammo: number): void {
  const children = [...pips.children]

  children.forEach((pip, index) => {
    pip.classList.toggle('is-spent', index >= ammo)
  })
}

/* --- 공개 API ------------------------------------------------------------------- */

/**
 * HUD를 만들어 `mount`에 붙인다. 반환된 핸들의 `dispose()`로만 정리한다.
 *
 * 카탈로그·팔레트를 인자로 받는 이유: HUD가 `catalog.ts`를 직접 import 하면
 * 방 템플릿마다 다른 소품 세트를 쓰는 순간(컨셉시트 4.1) 화면과 데이터가 갈린다.
 */
export function createHud(options: HudOptions): HudHandle {
  const root = createElement('div', 'hud-root')

  const { repaint, repaintAll } = createRepaintRegistry()

  const left = createElement('div', 'hud-left')
  const catalogPanel = createCatalogPanel(options.catalog, options.handlers, repaint)
  const palettePanel = createPalettePanel(options.palette, options.handlers, repaint)
  left.append(catalogPanel.panel, palettePanel.panel)

  const statusPanel = createElement('section', 'hud-panel hud-status')
  statusPanel.append(createPanelTitle('editor.statusTitle', repaint))
  const placedValue = createStatusRow(statusPanel, 'editor.statPlaced', repaint)
  const selectedValue = createStatusRow(statusPanel, 'editor.statSelected', repaint)
  const rotationValue = createStatusRow(statusPanel, 'editor.statFacing', repaint)
  const heightValue = createStatusRow(statusPanel, 'editor.statHeight', repaint)

  // 에디터 전용 패널을 한 겹으로 묶는다. 모드 전환은 클래스 하나로 끝나야 하고,
  // 패널마다 개별로 껐다 켜면 새 패널이 늘어날 때마다 전환 코드를 고쳐야 한다.
  const editorLayer = createElement('div', 'hud-editor')
  editorLayer.append(left, statusPanel, createKeysPanel(repaint))

  const mode = createModeBadge()
  const session = createSessionChip(repaint)
  const ammo = createAmmoGauge()

  const toast = createElement('div', 'hud-toast')
  // 거부 문구는 조작 직후에 뜬다. 스크린리더가 흐름을 끊지 않고 읽도록 polite로 둔다.
  toast.setAttribute('role', 'status')
  toast.setAttribute('aria-live', 'polite')

  root.append(editorLayer, mode.root, session.root, ammo.root, toast)
  options.mount.append(root)

  // 타이머 핸들은 도메인 상태가 아니라 정리해야 할 자원이다. 불변 규칙의 대상이 아니다.
  let toastTimer: number | null = null

  /*
   * 마지막으로 받은 뷰.
   *
   * "HUD는 상태를 갖지 않는다"는 이 파일의 계약을 깨는 것처럼 보이지만 그렇지 않다.
   * 금지되는 것은 **게임 상태**를 들고 있는 것이다("HUD에는 8개인데 실제로는 9개"가 그 실패다).
   * 여기 담기는 것은 마지막 렌더의 **입력**이고, 쓰이는 곳도 하나뿐이다 - 언어가 바뀌었을 때
   * 같은 값을 새 언어로 다시 그리는 것. 다음 `render`가 오면 그대로 덮인다.
   * 이게 없으면 언어를 바꾼 뒤 다음 조작이 있을 때까지 상태 표만 옛 언어로 남는다.
   */
  let lastView: HudView | null = null
  let lastGameView: GameHudView | null = null

  function showToast(text: string, isNotice: boolean): void {
    if (toastTimer !== null) clearTimeout(toastTimer)

    setText(toast, text)
    toast.classList.toggle('is-notice', isNotice)
    toast.classList.add('is-visible')

    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible')
      toastTimer = null
    }, TOAST_DURATION_MS)
  }

  function drawEditor(view: HudView): void {
    const isPlacing = view.toolKind === 'place'

    setActive(catalogPanel.selectButton, !isPlacing)
    for (const [id, button] of catalogPanel.itemButtons) {
      setActive(button, isPlacing && id === view.activeCatalogId)
    }

    // 스와치 인덱스가 곧 PaletteIndex다(palette.ts: "id는 배열 인덱스와 동일").
    palettePanel.swatches.forEach((swatch, index) => {
      setActive(swatch, index === view.activeColorId)
    })

    setText(placedValue, `${view.placedCount} / ${view.placementLimit}`)

    setText(selectedValue, view.selectedName ?? t(isPlacing ? 'editor.modePlace' : 'common.none'))
    selectedValue.classList.toggle('is-selected', view.selectedName !== null)

    // 잠긴 회전은 "왜 안 되는지"까지 말한다. 눌리지 않는 것과 고장난 것은 구별되지 않는다(4.3절).
    setText(rotationValue, t(view.isRotateLocked ? 'editor.facingLocked' : 'editor.facingFree'))
    rotationValue.classList.toggle('is-locked', view.isRotateLocked)

    const isFloating = view.isFreeHeight && view.freeHeightLevel !== null
    setText(
      heightValue,
      isFloating
        ? t('editor.heightFloating', { level: view.freeHeightLevel ?? 0 })
        : t('editor.heightOnSurface'),
    )
    heightValue.classList.toggle('is-on', view.isFreeHeight)
  }

  function drawGame(view: GameHudView): void {
    const isObserving = view.mode === 'observe'
    const message = MODE_MESSAGE[view.mode]

    setText(mode.label, t(message.label))
    setText(mode.hint, t(message.hint))
    mode.root.classList.toggle('is-observe', isObserving)

    // 배치 패널은 관찰 모드에서 통째로 사라진다. 반투명으로 남기지 않는 이유는
    // 화면 네 귀퉁이가 UI로 덮인 순간 그게 곧 "게임이 아니라 툴"로 읽히기 때문이고,
    // 폰에서는 그 면적이 곧 못 보는 방의 넓이라 더 직접적이다.
    editorLayer.classList.toggle('is-hidden', isObserving)
    // 탄약은 관찰 모드에서만 의미가 있다(꾸미는 중에는 쏘지 않는다).
    ammo.root.classList.toggle('is-hidden', !isObserving)

    setText(session.seed, view.seed)
    setText(session.count, String(view.objectCount))

    syncAmmoPips(ammo.pips, view.ammoMax)
    paintAmmoPips(ammo.pips, view.ammo)

    setText(ammo.value, String(view.ammo))
    setText(ammo.max, `/ ${view.ammoMax}`)
    ammo.root.classList.toggle('is-low', view.ammo > 0 && view.ammo <= LOW_AMMO_THRESHOLD)
    ammo.root.classList.toggle('is-empty', view.ammo <= 0)

    setText(ammo.status, view.lastShot === null ? '' : t(shotKey(view.lastShot)))
    ammo.status.dataset['shot'] = view.lastShot ?? ''
  }

  /*
   * 언어가 바뀌면 이 화면이 스스로 다시 그린다. 사전 엔진은 DOM을 건드리지 않으므로
   * (`setLocale`의 계약) 다시 그릴 시점을 아는 것은 화면 쪽 책임이다.
   * 토스트는 다시 칠하지 않는다 - 1.8초짜리라 언어를 바꾸는 동안 이미 사라져 있고,
   * `showNotice`의 글자는 부르는 쪽이 들고 있는 말이라 여기서 되살릴 방법도 없다.
   */
  const stopLocaleWatch = onLocaleChange(() => {
    repaintAll()
    if (lastView !== null) drawEditor(lastView)
    if (lastGameView !== null) drawGame(lastGameView)
  })

  return {
    render(view: HudView): void {
      lastView = view
      drawEditor(view)
    },

    renderGame(view: GameHudView): void {
      lastGameView = view
      drawGame(view)
    },

    setVisible(visible: boolean): void {
      // 숨길 때 토스트도 같이 끈다. 안 그러면 배치 모드에서 뜬 거부 문구가
      // 게임 모드로 넘어간 뒤에도 몇 초간 화면에 남아 수색 화면의 가짜 단서가 된다.
      if (!visible && toastTimer !== null) {
        clearTimeout(toastTimer)
        toastTimer = null
        toast.classList.remove('is-visible')
      }
      root.hidden = !visible
    },

    showRejection(reason: VerbRejection): void {
      showToast(t(rejectionKey(reason)), false)
    },

    showNotice(text: string): void {
      showToast(text, true)
    },

    dispose(): void {
      if (toastTimer !== null) clearTimeout(toastTimer)
      toastTimer = null
      // 구독을 끊지 않으면 떼어 낸 노드를 붙들고 있는 클로저가 사전 쪽에 영원히 남는다.
      stopLocaleWatch()
      root.remove()
    },
  }
}
